import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar, 
  Banknote, 
  TrendingUp, 
  Users,
  Copy,
  Share2,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Clock,
  DoorOpen,
  IdCard,
  Star,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/ui/stat-card";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/hooks/use-toast";
import { useBusiness } from "@/hooks/use-business";
import { useDashboardStats } from "@/hooks/use-dashboard-stats";
import { useMembershipDashboardStats } from "@/hooks/use-membership-dashboard-stats";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { StatusBadge } from "@/components/ui/status-badge";
import { getBaseUrl } from "@/lib/site";

export default function Dashboard() {
  const { toast } = useToast();
  const { business, loading: businessLoading } = useBusiness();
  const primaryModule = ((business as any)?.primary_module || "appointments") as string;
  const isMemberships = primaryModule === "memberships";
  const isCateringServices = primaryModule === "catering_services" || primaryModule === "food_vendors";
  // Default ON unless explicitly turned off
  const accessDeskEnabled = (business as any)?.access_desk_enabled !== false;
  const appointmentsInvoicingEnabled = (business as any)?.appointments_invoicing_enabled === true;
  const { stats: aptStats, loading: aptLoading } = useDashboardStats();
  const { stats: memStats, loading: memLoading } = useMembershipDashboardStats();
  const sb = supabase as any;

  const [foodLoading, setFoodLoading] = useState(false);
  const [foodStats, setFoodStats] = useState<{
    activeOrders: number;
    orders7d: number;
    revenue7d: number;
    outForDelivery: number;
    delivery7d: number;
  }>({
    activeOrders: 0,
    orders7d: 0,
    revenue7d: 0,
    outForDelivery: 0,
    delivery7d: 0,
  });
  const [recentFoodOrders, setRecentFoodOrders] = useState<any[]>([]);


  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [recentFeedback, setRecentFeedback] = useState<
    Array<{
      token: string;
      satisfied: boolean | null;
      rating: number | null;
      notes: string | null;
      service_name: string | null;
      customer_name: string | null;
      submitted_at: string | null;
    }>
  >([]);
  
  const bookingUrl = business?.slug ? `${getBaseUrl()}/${business.slug}` : "";

  const shareMessage = business?.name
    ? `Book with ${business.name}: ${bookingUrl}`
    : `Book here: ${bookingUrl}`;

  const copyBookingLink = () => {
    if (bookingUrl) {
      navigator.clipboard.writeText(bookingUrl);
      toast({
        title: "Link copied",
        description: "Booking link copied to clipboard",
      });
    }
  };

  const openShare = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleShareTwitter = () => {
    if (!bookingUrl) return;
    openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`);
  };

  const handleShareFacebook = () => {
    if (!bookingUrl) return;
    openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(bookingUrl)}`);
  };

  const handleShareWhatsApp = () => {
    if (!bookingUrl) return;
    openShare(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`);
  };

  const handleShareSnapchat = async () => {
    if (!bookingUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: business?.name ? `Book with ${business.name}` : "Book here",
          text: shareMessage,
          url: bookingUrl,
        });
        toast({ title: "Shared", description: "Thanks for sharing!" });
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          navigator.clipboard.writeText(shareMessage);
          toast({
            title: "Link copied",
            description: "Paste the link in Snapchat to share",
          });
        }
      }
    } else {
      navigator.clipboard.writeText(shareMessage);
      toast({
        title: "Link copied",
        description: "Paste the link in Snapchat to share",
      });
    }
  };

  useEffect(() => {
    const run = async () => {
      if (!isCateringServices || !business?.id) return;
      setFoodLoading(true);
      try {
        const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

        const isDelivery = (o: any) =>
          !!String(o?.delivery_address || "").trim() || !!String(o?.delivery_place_id || "").trim();

        const [
          { count: activeCount, error: aErr },
          { count: outCount, error: outErr },
          { data: last7, error: last7Err },
          { data: recent, error: rErr },
        ] = await Promise.all([
          sb
            .from("food_orders")
            .select("id", { count: "exact", head: true })
            .eq("business_id", business.id)
            .not("status", "in", '("delivered","cancelled","not_delivered")'),
          sb
            .from("food_orders")
            .select("id", { count: "exact", head: true })
            .eq("business_id", business.id)
            .eq("status", "out_for_delivery"),
          sb
            .from("food_orders")
            .select("id, total_amount, delivery_address, delivery_place_id, created_at")
            .eq("business_id", business.id)
            .gte("created_at", since)
            .order("created_at", { ascending: false })
            .limit(5000),
          sb
            .from("food_orders")
            .select(
              `
              id,
              confirmation_code,
              employee_name,
              created_at,
              status,
              total_amount,
              currency,
              delivery_address,
              delivery_place_id
            `,
            )
            .eq("business_id", business.id)
            .order("created_at", { ascending: false })
            .limit(5),
        ]);

        if (aErr) throw aErr;
        if (outErr) throw outErr;
        if (last7Err) throw last7Err;
        if (rErr) throw rErr;

        const last7Arr = last7 || [];
        const orders7d = last7Arr.length;
        const revenue7d = last7Arr.reduce((sum: number, o: any) => sum + Number(o?.total_amount || 0), 0);
        const delivery7d = last7Arr.filter(isDelivery).length;

        setFoodStats({
          activeOrders: Number(activeCount || 0),
          orders7d,
          revenue7d,
          outForDelivery: Number(outCount || 0),
          delivery7d,
        });
        setRecentFoodOrders(recent || []);
      } catch (e: any) {
        toast({ title: "Failed to load orders overview", description: e?.message || "Try again.", variant: "destructive" });
      } finally {
        setFoodLoading(false);
      }
    };
    run();
  }, [isCateringServices, business?.id]);


  useEffect(() => {
    const run = async () => {
      if (!business?.id) return;
      if (isMemberships || isCateringServices) return;
      setFeedbackLoading(true);
      try {
        const { data, error } = await (supabase as any)
          .from("service_feedback_requests")
          .select("token,satisfied,rating,notes,service_name,customer_name,submitted_at")
          .eq("business_id", business.id)
          .not("submitted_at", "is", null)
          .order("submitted_at", { ascending: false })
          .limit(5);
        if (error) throw error;
        setRecentFeedback((data as any[]) || []);
      } catch {
        setRecentFeedback([]);
      } finally {
        setFeedbackLoading(false);
      }
    };
    run();
  }, [business?.id, isMemberships, isCateringServices]);

  const isLoadingUnified = businessLoading || (isMemberships ? memLoading : isCateringServices ? foodLoading : aptLoading);

  const formatCurrency = (amount: number) => {
    const currency = business?.currency || "USD";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Overview"
        action={
          isMemberships ? (
            <div className="flex gap-2">
              <Button asChild size="pill" variant="outline">
                <Link to="/app/memberships">
                  <IdCard className="mr-2 h-4 w-4" />
                  Memberships
                </Link>
              </Button>
              {accessDeskEnabled ? (
                <Button asChild size="pill">
                  <Link to="/app/access-desk">
                    <DoorOpen className="mr-2 h-4 w-4" />
                    Access Desk
                  </Link>
                </Button>
              ) : null}
            </div>
          ) : isCateringServices ? (
            <div className="flex gap-2">
              <Button asChild size="pill" variant="outline">
                <Link to="/app/invoicing">
                  <Banknote className="mr-2 h-4 w-4" />
                  Invoicing
                </Link>
              </Button>
              <Button asChild size="pill">
                <Link to="/app/orders">
                  <Calendar className="mr-2 h-4 w-4" />
                  Orders
                </Link>
              </Button>
            </div>
          ) : (
            <Button asChild size="pill">
              <Link to="/app/appointments">
                <Calendar className="mr-2 h-4 w-4" />
                View Appointments
              </Link>
            </Button>
          )
        }
      />

      {isLoadingUnified ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          {isMemberships ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <StatCard
                title="Active Members"
                description="Currently active memberships"
                value={memStats.activeMembers.toString()}
                icon={Users}
              />
              <StatCard
                title="Expiring Soon"
                description="Next 7 days"
                value={memStats.expiringSoon.toString()}
                icon={AlertCircle}
              />
              <StatCard
                title="Visits Today"
                description="Members + walk-ins"
                value={memStats.visitsToday.toString()}
                icon={DoorOpen}
              />
              <StatCard
                title="Walk-in Revenue (Today)"
                description="Day passes"
                value={formatCurrency(memStats.walkInRevenueToday)}
                icon={Banknote}
              />
              <StatCard
                title="Membership Revenue"
                description="Renewals this month"
                value={formatCurrency(memStats.membershipRevenueThisMonth)}
                icon={TrendingUp}
              />
            </div>
          ) : isCateringServices ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard
                title="Active orders"
                description="Not delivered/cancelled"
                value={foodStats.activeOrders.toString()}
                icon={CheckCircle2}
              />
              <StatCard
                title="Orders (7 days)"
                description="New orders received"
                value={foodStats.orders7d.toString()}
                icon={Users}
              />
              <StatCard
                title="Revenue (7 days)"
                description="Total order value"
                value={formatCurrency(foodStats.revenue7d)}
                icon={Banknote}
              />
              <StatCard
                title="Out for delivery"
                description="Currently in transit"
                value={foodStats.outForDelivery.toString()}
                icon={TrendingUp}
              />
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                <StatCard
                  title="Monthly Appointments"
                  description="Total appointments this month"
                  value={aptStats.monthlyAppointments.toString()}
                  icon={Calendar}
                />
                <StatCard
                  title="Total Revenue"
                  description="All revenue sources this month"
                  value={formatCurrency(aptStats.totalRevenue)}
                  icon={Banknote}
                />
                <StatCard
                  title="Projected Revenue"
                  description="Including confirmed appointments"
                  value={formatCurrency(aptStats.projectedRevenue)}
                  icon={TrendingUp}
                />
                <StatCard
                  title="Downpayments Received"
                  description="Deposits paid this month"
                  value={formatCurrency(aptStats.downpaymentsReceived)}
                  icon={Banknote}
                />
                <StatCard
                  title="New Customers"
                  description="First-time appointments"
                  value={aptStats.newCustomers.toString()}
                  icon={Users}
                />
              </div>
              {/* Revenue Breakdown */}
              {((appointmentsInvoicingEnabled && aptStats.invoiceRevenue > 0) || aptStats.walkInRevenue > 0 || aptStats.membershipRevenue > 0) && (
                <div className="mb-8 p-4 rounded-lg border border-border bg-card">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Revenue Breakdown</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="text-sm">
                      <div className="text-muted-foreground">Appointments</div>
                      <div className="text-lg font-semibold text-foreground">{formatCurrency(aptStats.monthlyRevenue)}</div>
                    </div>
                    {appointmentsInvoicingEnabled && aptStats.invoiceRevenue > 0 && (
                      <div className="text-sm">
                        <div className="text-muted-foreground">Invoices</div>
                        <div className="text-lg font-semibold text-foreground">{formatCurrency(aptStats.invoiceRevenue)}</div>
                      </div>
                    )}
                    {aptStats.walkInRevenue > 0 && (
                      <div className="text-sm">
                        <div className="text-muted-foreground">Walk-ins</div>
                        <div className="text-lg font-semibold text-foreground">{formatCurrency(aptStats.walkInRevenue)}</div>
                      </div>
                    )}
                    {aptStats.membershipRevenue > 0 && (
                      <div className="text-sm">
                        <div className="text-muted-foreground">Memberships</div>
                        <div className="text-lg font-semibold text-foreground">{formatCurrency(aptStats.membershipRevenue)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Action Required */}
          {!isMemberships && !isCateringServices && aptStats.pendingAppointments > 0 && (
            <div className="bg-warning-bg border border-warning/20 rounded-xl p-4 mb-6 glow-edge">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-warning" />
                  <div>
                    <p className="font-medium text-warning-foreground">Action Required</p>
                    <p className="text-sm text-warning-foreground/80">You have {aptStats.pendingAppointments} appointments awaiting confirmation</p>
                  </div>
                </div>
                <Button variant="outline" asChild>
                  <Link to="/app/appointments">Review Appointments</Link>
                </Button>
              </div>
            </div>
          )}

          {/* Upcoming Appointments OR Recent Visits */}
          {isMemberships ? (
            <div className="bg-card rounded-xl border border-border shadow-soft mb-6">
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-2">
                  <DoorOpen className="h-5 w-5 text-primary" />
                  <h2 className="font-semibold text-foreground">Recent Visits</h2>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {memStats.recentVisits.length === 0 ? "No visits yet" : `Latest ${memStats.recentVisits.length} check-ins`}
                </p>
              </div>
              <div className="p-6">
                {memStats.recentVisits.length > 0 ? (
                  <div className="space-y-3">
                    {memStats.recentVisits.map((v) => (
                      <div key={v.id} className="p-3 sm:p-4 rounded-lg border border-border bg-background">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm flex-shrink-0">
                              {v.customer_name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm sm:text-base text-foreground leading-tight truncate">
                                {v.customer_name}
                              </p>
                              <p className="text-xs sm:text-sm text-muted-foreground leading-tight truncate mt-0.5">
                                {v.kind === "member" ? `Member • ${v.label}` : `Walk-in • ${v.label}`}
                                {v.party_size > 1 ? ` • Party ${v.party_size}` : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <div className="text-right leading-tight">
                              <p className="text-xs sm:text-sm font-medium text-foreground">
                                {format(new Date(v.check_in_at), "MMM d, yyyy")}
                              </p>
                              <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground mt-0.5">
                                <Clock className="h-3 w-3" />
                                {format(new Date(v.check_in_at), "h:mm a")}
                              </div>
                            </div>
                            <StatusBadge status={v.check_out_at ? "completed" : "pending"} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={DoorOpen} title="No visits yet" description="Visits from the Access Desk will appear here" />
                )}
              </div>
            </div>
          ) : isCateringServices ? (
            <div className="bg-card rounded-xl border border-border shadow-soft mb-6">
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <h2 className="font-semibold text-foreground">Recent orders</h2>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {recentFoodOrders.length === 0 ? "No orders yet" : `Latest ${recentFoodOrders.length} orders (up to 5)`}
                </p>
              </div>
              <div className="p-6">
                {recentFoodOrders.length > 0 ? (
                  <div className="space-y-3">
                    {recentFoodOrders.map((o) => {
                      const status = String(o.status || "new");
                      const statusLabel = status.replace(/_/g, " ");
                      const isPast = ["delivered", "cancelled", "not_delivered"].includes(status);
                      const isDelivery = !!String(o.delivery_address || "").trim() || !!String(o.delivery_place_id || "").trim();
                      const fulfillmentLabel = isDelivery ? "Delivery" : "Pickup";
                      const statusClass =
                        status === "delivered"
                          ? "bg-success-bg text-success border-success/20"
                          : status === "cancelled" || status === "not_delivered"
                            ? "bg-destructive/10 text-destructive border-destructive/20"
                            : status === "ready"
                              ? "bg-success-bg text-success border-success/20"
                              : status === "preparing"
                                ? "bg-warning-bg text-warning-foreground border-warning/20"
                                : "bg-primary/10 text-primary border-primary/20";
                      const fulfillmentClass = isDelivery
                        ? "bg-primary/10 text-primary border-primary/20"
                        : "bg-muted text-muted-foreground border-border";

                      return (
                        <div key={o.id} className="p-3 sm:p-4 rounded-lg border border-border bg-background">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm sm:text-base text-foreground leading-tight truncate">
                              {String(o.employee_name || "Customer")}
                            </p>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-tight truncate mt-0.5">
                              {String(o.confirmation_code || "—")}
                              {" • "}
                              {fulfillmentLabel}
                              {Number(o.total_amount || 0) > 0 ? ` • ${formatCurrency(Number(o.total_amount || 0))}` : ""}
                            </p>
                            {isPast ? (
                              <p className="text-[11px] text-muted-foreground mt-1 capitalize">Finalized</p>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">
                            <Clock className="h-3 w-3" />
                            {o.created_at ? format(new Date(o.created_at), "MMM d, h:mm a") : "—"}
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${fulfillmentClass}`}>
                            {fulfillmentLabel}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${statusClass}`}>
                            {statusLabel}
                          </span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState icon={Users} title="No orders yet" description="Orders from your public ordering page will appear here" />
                )}
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border shadow-soft mb-6">
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <h2 className="font-semibold text-foreground">Upcoming Appointments</h2>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {aptStats.upcomingAppointments.length === 0
                    ? "No upcoming appointments yet"
                    : `Showing ${aptStats.upcomingAppointments.length} upcoming appointment${
                        aptStats.upcomingAppointments.length === 1 ? "" : "s"
                      } (up to 5)`}
                </p>
              </div>
              <div className="p-6">
                {aptStats.upcomingAppointments.length > 0 ? (
                  <div className="space-y-3">
                    {aptStats.upcomingAppointments.map((apt) => (
                      <div key={apt.id} className="p-3 sm:p-4 rounded-lg border border-border bg-background">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm flex-shrink-0">
                              {apt.customer_name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm sm:text-base text-foreground leading-tight truncate">
                                {apt.customer_name}
                              </p>
                              <p className="text-xs sm:text-sm text-muted-foreground leading-tight truncate mt-0.5">
                                {apt.service_name}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <div className="text-right leading-tight">
                              <p className="text-xs sm:text-sm font-medium text-foreground">
                                {format(new Date(apt.start_datetime), "MMM d, yyyy")}
                              </p>
                              <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground mt-0.5">
                                <Clock className="h-3 w-3" />
                                {format(new Date(apt.start_datetime), "h:mm a")}
                              </div>
                            </div>
                            <StatusBadge status={apt.status as "pending" | "confirmed"} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={Calendar} title="No upcoming appointments" description="Your upcoming appointments will appear here" />
                )}
              </div>
            </div>
          )}

          {/* Service Reviews */}
          {!isMemberships && !isCateringServices ? (
            <div className="bg-card rounded-xl border border-border shadow-soft mb-6">
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <h2 className="font-semibold text-foreground">Service Reviews</h2>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {feedbackLoading
                    ? "Loading recent reviews…"
                    : recentFeedback.length === 0
                      ? "No reviews submitted yet"
                      : `Latest ${recentFeedback.length} review${recentFeedback.length === 1 ? "" : "s"}`}
                </p>
              </div>
              <div className="p-6">
                {recentFeedback.length > 0 ? (
                  <div className="space-y-3">
                    {recentFeedback.map((r) => {
                      const rating = Math.max(0, Math.min(5, Number(r.rating || 0)));
                      const serviceName = String(r.service_name || "Service");
                      const customerName = String(r.customer_name || "Customer");
                      const notes = String(r.notes || "").trim();
                      const satisfied =
                        r.satisfied === null || r.satisfied === undefined ? null : Boolean(r.satisfied);
                      return (
                        <div key={String(r.token)} className="p-3 sm:p-4 rounded-lg border border-border bg-background">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-sm sm:text-base text-foreground leading-tight truncate">
                                {serviceName}
                              </p>
                              <p className="text-xs sm:text-sm text-muted-foreground leading-tight truncate mt-0.5">
                                {customerName}
                                {r.submitted_at ? ` • ${format(new Date(r.submitted_at), "MMM d, yyyy")}` : ""}
                                {satisfied === null ? "" : satisfied ? " • Satisfied" : " • Not satisfied"}
                              </p>
                              {notes ? (
                                <p className="text-sm text-foreground/90 mt-2 whitespace-pre-wrap">{notes}</p>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {Array.from({ length: 5 }).map((_, idx) => (
                                <Star
                                  key={idx}
                                  className={`h-4 w-4 ${
                                    idx < rating ? "text-yellow-500 fill-current" : "text-muted-foreground fill-none"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    icon={Star}
                    title="No reviews yet"
                    description="When clients submit a review after their appointment, it will appear here."
                  />
                )}
              </div>
            </div>
          ) : null}


          {/* Booking Link */}
          {!isMemberships && !isCateringServices && business?.slug && (
            <div className="bg-accent rounded-xl border border-border p-6">
              <h2 className="font-semibold text-foreground mb-2">Your Booking Link</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Share this link with your customers to let them book appointments
              </p>
              <div className="flex items-center gap-2 bg-background rounded-lg border border-border p-3">
                <input
                  type="text"
                  value={bookingUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-foreground outline-none"
                />
                <Button variant="ghost" size="icon" onClick={copyBookingLink}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" asChild>
                  <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleShareTwitter}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share on Twitter
                </Button>
                <Button variant="outline" size="sm" onClick={handleShareFacebook}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share on Facebook
                </Button>
                <Button variant="outline" size="sm" onClick={handleShareWhatsApp}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share on WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={handleShareSnapchat}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share on Snapchat
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
